import {
  DocumentSnapshot,
  Firestore,
  User,
  getAuth,
} from "@atgof-firebase/firebase";
import { IdTokenResult } from "firebase/auth";
import { MembershipCategory } from "../common/model/project/membership";
import {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LanguageContext } from "@atgof-common/language";
import { useSnapshot } from "./useSnapshot";
import { Unsubscribe } from "@atgof-common/onSnapshot";

export function getUserRef(db: Firestore, uid: string) {
  return db.doc(`users/${uid}`);
}

function onUserSnapshot(
  db: Firestore | undefined,
  user: User | null,
  languageRef: MutableRefObject<string>,
  onNext: (doc: DocumentSnapshot | undefined) => void,
) {
  onNext(undefined);
  if (!(db && user?.uid)) return;
  const ref = db.collection("users").doc(user.uid);
  let cancelled = false;
  let unsub: Unsubscribe | undefined = undefined;
  ref.get().then((u) => {
    if (cancelled) return;
    if (
      u?.get("displayName") === user.displayName &&
      u?.get("email") === user.email &&
      u?.get("language") &&
      u?.get("projectOwnershipQuota") != null
    ) {
      unsub = ref.onSnapshot(onNext);
    } else {
      ref.set(
        {
          displayName: user.displayName,
          email: user.email,
          language: u?.get("language") ?? languageRef.current,
          projectOwnershipQuota: 0,
        },
        { merge: true },
      );
      unsub = ref.onSnapshot(onNext);
    }
  });
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}

function logOut() {
  return getAuth().signOut();
}

export function useUserState(db: Firestore | undefined) {
  const { language, setLanguage } = useContext(LanguageContext);
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const [authUser, setAuthUser] = useState<User | null>(null);
  useEffect(() => {
    if (!db) return;
    return getAuth().onAuthStateChanged(setAuthUser);
  }, [!db]);

  const [idTokenResult, setIdTokenResult] = useState<
    IdTokenResult | undefined
  >();

  const [emailVerified, setEmailVerified] = useState(authUser?.emailVerified);

  useEffect(() => {
    setEmailVerified(authUser?.emailVerified);
    let cancelled = false;
    if (!authUser || authUser.emailVerified) return;
    const timeout = setInterval(async () => {
      try {
        if (cancelled) return;
        if (authUser.emailVerified) {
          setEmailVerified(true);
          return;
        }
        await authUser.reload();
        if (cancelled || !authUser.emailVerified) return;
        // Refresh as per https://github.com/firebase/firebase-js-sdk/issues/2529#issuecomment-575722149
        await authUser.getIdToken(true);
        if (!cancelled) setEmailVerified(true);
      } catch (err) {
        console.error(err);
      }
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(timeout);
    };
  }, [authUser]);
  useEffect(() => {
    let cancelled = false;
    setIdTokenResult(undefined);
    authUser
      ?.getIdTokenResult()
      .then((idToken) => {
        if (!cancelled) setIdTokenResult(idToken);
      })
      .catch(() => setIdTokenResult(undefined));
    return () => {
      cancelled = true;
    };
  }, [authUser]);

  const user = useSnapshot<DocumentSnapshot | undefined>(
    (onNext) => onUserSnapshot(db, authUser, languageRef, onNext),
    [db, authUser, emailVerified],
  );

  useEffect(() => {
    if (user) setLanguage(user.get("language") ?? language);
  }, [user]);

  return useMemo(
    () =>
      authUser
        ? { user, authUser, idTokenResult, logOut, emailVerified }
        : undefined,
    [user, authUser, emailVerified],
  );
}

export function roleCategories(roles: any, kind: "admin" | "user" | "all") {
  const filterRoles =
    kind == "all"
      ? (k: string) => roles[k]["user"] === true || roles[k]["admin"] === true
      : (k: string) => roles[k][kind] === true;
  return roles
    ? (Object.keys(roles).filter(filterRoles) as MembershipCategory[])
    : [];
}
